import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Form, Dropdown } from '@openedx/paragon';

import SectionSubHeader from '../../generic/section-sub-header';
import messages from './messages';

const DetailsSection = ({
  language, languageOptions, courseFor, providers, onChange,
}) => {
  const intl = useIntl();

  // Normalize courseFor to always be an array internally
  const normalizedCourseFor = React.useMemo(() => {
    if (!courseFor) return ['public'];
    if (Array.isArray(courseFor)) {
      return courseFor.length > 0 ? courseFor : ['public'];
    }
    // Handle comma-separated string from backend
    if (typeof courseFor === 'string') {
      const items = courseFor.split(',').map(item => item.trim()).filter(Boolean);
      return items.length > 0 ? items : ['public'];
    }
    return ['public'];
  }, [courseFor]);

  const formattedLanguage = () => {
    const result = languageOptions.find((arr) => arr[0] === language);
    return result ? result[1] : intl.formatMessage(messages.dropdownEmpty);
  };

  // eslint-disable-next-line arrow-body-style
  const formattedCourseFor = () => {
    return normalizedCourseFor.join(', ');
  };

  // eslint-disable-next-line arrow-body-style
  const isCourseForSelected = (provider) => {
    return normalizedCourseFor.includes(provider);
  };

  const handleCourseForChange = (provider) => {
    let newCourseFor;

    if (provider === 'public') {
      // If public is clicked, select only public (deselect all others)
      newCourseFor = ['public'];
    } else {
      // If a non-public provider is clicked
      if (normalizedCourseFor.includes(provider)) {
        // Remove the provider if already selected
        newCourseFor = normalizedCourseFor.filter((item) => item !== provider && item !== 'public');
      } else {
        // Add the provider and remove public
        newCourseFor = normalizedCourseFor.filter((item) => item !== 'public');
        newCourseFor = [...newCourseFor, provider];
      }

      // If no providers are selected, default to public
      if (newCourseFor.length === 0) {
        newCourseFor = ['public'];
      }
    }

    onChange(newCourseFor, 'courseFor');
  };

  return (
    <section className="section-container details-section">
      <SectionSubHeader
        title={intl.formatMessage(messages.detailsTitle)}
        description={intl.formatMessage(messages.detailsDescription)}
      />
      <Form.Group className="form-group-custom dropdown-language">
        <Form.Label>{intl.formatMessage(messages.dropdownLabel)}</Form.Label>
        <Dropdown className="bg-white">
          <Dropdown.Toggle variant="outline-primary" id="languageDropdown">
            {formattedLanguage()}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {languageOptions.map((option) => (
              <Dropdown.Item
                key={option[0]}
                onClick={() => onChange(option[0], 'language')}
              >
                {option[1]}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <Form.Control.Feedback>
          {intl.formatMessage(messages.dropdownHelpText)}
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="form-group-custom checkbox-course-for">
        <Form.Label>{intl.formatMessage(messages.courseForLabel)}</Form.Label>
        <div className="d-flex flex-column bg-white p-3 border rounded">
          <div className="mb-2 text-muted" style={{ fontSize: '0.9rem' }}>
            {formattedCourseFor()}
          </div>
          {providers.map((provider) => (
            <Form.Check
              key={provider}
              type="checkbox"
              id={`courseFor-${provider}`}
              label={provider}
              checked={isCourseForSelected(provider)}
              onChange={() => handleCourseForChange(provider)}
              className="mb-2"
            />
          ))}
        </div>
        <Form.Control.Feedback>
          {intl.formatMessage(messages.courseForHelpText)}
        </Form.Control.Feedback>
      </Form.Group>
    </section>
  );
};

DetailsSection.defaultProps = {
  language: '',
  courseFor: ['public'],
};

DetailsSection.propTypes = {
  language: PropTypes.string,
  languageOptions: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  ).isRequired,
  courseFor: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string.isRequired),
  ]),
  providers: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DetailsSection;
